import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import StatusPill from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { endpoints } from "@/lib/api";
import { fmtMoney, fmtDate, todayISO } from "@/lib/utils-format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CheckCircle2, Receipt } from "lucide-react";

const STATUSES = ["Pending", "Confirmed", "In Progress", "Completed", "Cancelled"];

export default function Jobs() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [search] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    const [j, c, e] = await Promise.all([endpoints.list("jobs"), endpoints.list("clients"), endpoints.list("employees")]);
    setItems(j.data); setClients(c.data); setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (search.get("new") === "1") {
      const c = clients.find(x => x.id === search.get("client"));
      setEditing(null);
      setForm({
        client_id: c?.id || "", client_name: c ? `${c.first_name} ${c.last_name}` : "",
        address: c ? `${c.street}, ${c.city}, ${c.state} ${c.zip}` : "",
        service: "", crew: "Crew A", equipment: "", materials: "",
        date: todayISO(), time: "09:00",
        priority: "Normal", status: "Pending", price: 0, notes: "",
      });
      setOpen(true);
    }
  }, [search, clients]);

  const startNew = () => {
    setEditing(null);
    setForm({
      client_id: "", client_name: "", address: "", service: "",
      crew: "Crew A", equipment: "", materials: "",
      date: todayISO(), time: "09:00",
      priority: "Normal", status: "Pending", price: 0, notes: "",
    });
    setOpen(true);
  };
  const startEdit = (j) => { setEditing(j); setForm(j); setOpen(true); };
  const onClientChange = (id) => {
    const c = clients.find(x => x.id === id);
    setForm({ ...form, client_id: id, client_name: c ? `${c.first_name} ${c.last_name}` : "", address: c ? `${c.street}, ${c.city}, ${c.state} ${c.zip}` : "" });
  };
  const save = async () => {
    if (editing) await endpoints.update("jobs", editing.id, form);
    else await endpoints.create("jobs", form);
    toast.success("Job saved"); setOpen(false); load();
  };
  const complete = async (j) => { await endpoints.update("jobs", j.id, { ...j, status: "Completed" }); toast.success("Job completed"); load(); };
  const createInvoice = (j) => {
    navigate(`/invoices?new=1&job=${j.id}`);
  };
  const remove = async (id) => { if(!window.confirm("Delete?"))return; await endpoints.remove("jobs", id); load(); };

  return (
    <Layout title="Jobs" subtitle={`${items.length} jobs scheduled`}>
      <div className="flex justify-end mb-4"><Button onClick={startNew} data-testid="jobs-new"><Plus className="size-4 mr-1.5"/>New Job</Button></div>
      <Card className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="jobs-table">
          <thead className="bg-muted/40"><tr className="text-xs uppercase tracking-wider text-muted-foreground text-left">
            <th className="p-4 font-semibold">Date</th><th className="p-4 font-semibold">Customer</th>
            <th className="p-4 font-semibold">Service</th><th className="p-4 font-semibold">Crew</th>
            <th className="p-4 font-semibold">Priority</th><th className="p-4 font-semibold">Price</th>
            <th className="p-4 font-semibold">Status</th><th className="p-4 font-semibold w-40">Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="8" className="p-10 text-center text-muted-foreground">No jobs yet.</td></tr>}
            {items.map(j => (
              <tr key={j.id} className="border-t border-border hover:bg-muted/30">
                <td className="p-4"><div className="font-medium">{fmtDate(j.date)}</div><div className="text-xs text-muted-foreground">{j.time}</div></td>
                <td className="p-4">{j.client_name}<div className="text-xs text-muted-foreground">{j.address}</div></td>
                <td className="p-4">{j.service}</td>
                <td className="p-4">{j.crew}</td>
                <td className="p-4"><StatusPill status={j.priority === "High" ? "Estimate Scheduled" : "Completed"}/></td>
                <td className="p-4 font-mono font-semibold">{fmtMoney(j.price)}</td>
                <td className="p-4"><StatusPill status={j.status}/></td>
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="size-7" onClick={()=>startEdit(j)}><Pencil className="size-3.5"/></Button>
                    {j.status !== "Completed" && <Button size="icon" variant="ghost" className="size-7 text-secondary" onClick={()=>complete(j)} title="Complete"><CheckCircle2 className="size-3.5"/></Button>}
                    {j.status === "Completed" && <Button size="icon" variant="ghost" className="size-7" onClick={()=>createInvoice(j)} title="Create Invoice"><Receipt className="size-3.5"/></Button>}
                    <Button size="icon" variant="ghost" className="size-7 text-destructive" onClick={()=>remove(j.id)}><Trash2 className="size-3.5"/></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-form">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit Job" : "New Job"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <div className="col-span-2 space-y-1.5"><Label>Customer</Label>
              <Select value={form.client_id||""} onValueChange={onClientChange}>
                <SelectTrigger data-testid="job-client"><SelectValue placeholder="Select client"/></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.address||""} onChange={(e)=>setForm({...form, address:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Service</Label><Input value={form.service||""} onChange={(e)=>setForm({...form, service:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Crew</Label>
              <Select value={form.crew||"Crew A"} onValueChange={(v)=>setForm({...form, crew:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Crew A","Crew B","Design","Solo"].map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date||""} onChange={(e)=>setForm({...form, date:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Time</Label><Input type="time" value={form.time||""} onChange={(e)=>setForm({...form, time:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Equipment</Label><Input value={form.equipment||""} onChange={(e)=>setForm({...form, equipment:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Materials</Label><Input value={form.materials||""} onChange={(e)=>setForm({...form, materials:e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Priority</Label>
              <Select value={form.priority||"Normal"} onValueChange={(v)=>setForm({...form, priority:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{["Low","Normal","High"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={form.status||"Pending"} onValueChange={(v)=>setForm({...form, status:v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Price ($)</Label><Input type="number" step="0.01" value={form.price||0} onChange={(e)=>setForm({...form, price:Number(e.target.value)})}/></div>
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes||""} onChange={(e)=>setForm({...form, notes:e.target.value})}/></div>

            {/* Employee Pay section */}
            <div className="col-span-2 mt-2 border-t border-border pt-4">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Employee Pay</Label>
                <Button size="sm" variant="outline" type="button" onClick={() => {
                  const arr = form.employees || [];
                  const _aid = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                  setForm({ ...form, employees: [...arr, { _aid, employee_id: "", name: "", pay_type: "hourly", hours: 0, fixed_pay: 0, hourly_rate: 0 }] });
                }} data-testid="job-add-employee"><Plus className="size-3.5 mr-1"/>Assign Employee</Button>
              </div>
              {(form.employees || []).length === 0 && (
                <div className="text-xs text-muted-foreground italic mb-2">No employees assigned. Add one to track payroll for this job.</div>
              )}
              {(form.employees || []).map((a, i) => {
                const update = (k, v) => {
                  const arr = [...form.employees];
                  arr[i] = { ...arr[i], [k]: v };
                  if (k === "employee_id") {
                    const emp = employees.find((e) => e.id === v);
                    if (emp) {
                      arr[i].name = `${emp.first_name} ${emp.last_name}`;
                      arr[i].hourly_rate = Number(emp.pay_rate || 0);
                    }
                  }
                  setForm({ ...form, employees: arr });
                };
                const removeAssignment = () => setForm({ ...form, employees: form.employees.filter((_, idx) => idx !== i) });
                const pay = a.pay_type === "fixed"
                  ? Number(a.fixed_pay || 0)
                  : Number(a.hours || 0) * Number(a.hourly_rate || 0);
                return (
                  <div key={a._aid || `idx-${i}`} className="grid grid-cols-12 gap-2 items-end mb-2 p-3 rounded-lg bg-muted/30" data-testid={`job-employee-${i}`}>
                    <div className="col-span-12 sm:col-span-4 space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider">Employee</Label>
                      <Select value={a.employee_id || ""} onValueChange={(v)=>update("employee_id", v)}>
                        <SelectTrigger className="h-8"><SelectValue placeholder="Select"/></SelectTrigger>
                        <SelectContent>{employees.map(emp => <SelectItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-6 sm:col-span-3 space-y-1">
                      <Label className="text-[10px] uppercase tracking-wider">Pay Type</Label>
                      <Select value={a.pay_type || "hourly"} onValueChange={(v)=>update("pay_type", v)}>
                        <SelectTrigger className="h-8"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {a.pay_type === "fixed" ? (
                      <div className="col-span-5 sm:col-span-3 space-y-1">
                        <Label className="text-[10px] uppercase tracking-wider">Fixed Pay ($)</Label>
                        <Input className="h-8" type="number" step="0.01" value={a.fixed_pay || 0} onChange={(e)=>update("fixed_pay", Number(e.target.value))}/>
                      </div>
                    ) : (
                      <>
                        <div className="col-span-3 sm:col-span-2 space-y-1">
                          <Label className="text-[10px] uppercase tracking-wider">Hours</Label>
                          <Input className="h-8" type="number" step="0.25" value={a.hours || 0} onChange={(e)=>update("hours", Number(e.target.value))}/>
                        </div>
                        <div className="col-span-3 sm:col-span-2 space-y-1">
                          <Label className="text-[10px] uppercase tracking-wider">Rate</Label>
                          <Input className="h-8" type="number" step="0.5" value={a.hourly_rate || 0} onChange={(e)=>update("hourly_rate", Number(e.target.value))}/>
                        </div>
                      </>
                    )}
                    <div className="col-span-10 sm:col-span-1 text-right font-mono text-sm font-semibold pb-1">{fmtMoney(pay)}</div>
                    <Button size="icon" variant="ghost" type="button" className="col-span-2 sm:col-span-1 size-8 text-destructive justify-self-end" onClick={removeAssignment}><Trash2 className="size-3.5"/></Button>
                  </div>
                );
              })}
              {(form.employees || []).length > 0 && (
                <div className="mt-2 p-3 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider font-semibold text-primary">Total Employee Pay (this job)</span>
                  <span className="font-mono font-bold">{fmtMoney((form.employees || []).reduce((s, a) => s + (a.pay_type === "fixed" ? Number(a.fixed_pay || 0) : Number(a.hours || 0) * Number(a.hourly_rate || 0)), 0))}</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="mt-4"><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="job-save">Save Job</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
